import { useState, useMemo } from 'react';
import { Lock, Ban } from 'lucide-react';
import { useCurrentUserWording } from '@/hooks/useVendorWording';

interface Permission {
  code: string;
  category: string;
  label: string;
  description?: string;
}

interface PermissionsSelectorProps {
  availablePermissions: Permission[];
  selectedPermissions: string[];
  onChange: (permissions: string[]) => void;
  showPermissionCode?: boolean;
  /** Permissions automatiquement activées quand une permission source est sélectionnée */
  impliedPermissions?: Record<string, string[]>;
  /** Prérequis : une permission nécessite qu'au moins une des permissions listées soit active */
  prerequisitePermissions?: Record<string, string[]>;
}

/**
 * Permissions impliquées par défaut :
 * dashboard.view_all → contract.view_all, delivery_proof.view_all, sav.view_all
 * *.view_all → *.list
 */
export const DEFAULT_IMPLIED_PERMISSIONS: Record<string, string[]> = {
  'dashboard.view_all': ['contract.view_all', 'delivery_proof.view_all', 'sav.view_all'],
  'contract.view_all': ['contract.list'],
  'delivery_proof.view_all': ['delivery_proof.list'],
  'sav.view_all': ['sav.list'],
};

/**
 * Prérequis par défaut :
 * dashboard.view_all nécessite au moins une autre permission dashboard
 */
export const DEFAULT_PREREQUISITE_PERMISSIONS: Record<string, string[]> = {
  'dashboard.view_all': [
    'dashboard.view_contracts', 'dashboard.view_revenue',
    'dashboard.view_sav_claims', 'dashboard.view_sav_amounts',
    'dashboard.view_provision', 'dashboard.view_encaissements', 'dashboard.view_synthese',
  ],
};

export const PermissionsSelector = ({
  availablePermissions,
  selectedPermissions,
  onChange,
  showPermissionCode = false,
  impliedPermissions = DEFAULT_IMPLIED_PERMISSIONS,
  prerequisitePermissions = DEFAULT_PREREQUISITE_PERMISSIONS,
}: PermissionsSelectorProps) => {
  const wording = useCurrentUserWording();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Grouper par catégorie, *.view_all en fin de liste
  const groupedPermissions = useMemo(() => {
    const groups: Record<string, Permission[]> = {};

    availablePermissions.forEach(perm => {
      if (!groups[perm.category]) {
        groups[perm.category] = [];
      }
      groups[perm.category].push(perm);
    });

    // Trier : permissions normales d'abord, *.view_all en fin
    for (const category of Object.keys(groups)) {
      groups[category].sort((a, b) => {
        const aIsViewAll = a.code.endsWith('.view_all');
        const bIsViewAll = b.code.endsWith('.view_all');
        if (aIsViewAll === bIsViewAll) return 0;
        return aIsViewAll ? 1 : -1;
      });
    }

    return groups;
  }, [availablePermissions]);

  // Calculer les permissions forcées par les implications (transitif)
  const forcedPermissions = useMemo(() => {
    const forced = new Set<string>();
    const sources = new Set(selectedPermissions);
    let changed = true;
    while (changed) {
      changed = false;
      for (const [source, implied] of Object.entries(impliedPermissions)) {
        if (sources.has(source)) {
          for (const p of implied) {
            if (!forced.has(p)) {
              forced.add(p);
              sources.add(p);
              changed = true;
            }
          }
        }
      }
    }
    return forced;
  }, [selectedPermissions, impliedPermissions]);

  // Permissions dont les prérequis ne sont pas remplis (disabled + non cochable)
  const missingPrerequisites = useMemo(() => {
    const missing = new Set<string>();
    const effective = new Set(selectedPermissions);
    forcedPermissions.forEach(p => effective.add(p));

    for (const [perm, prereqs] of Object.entries(prerequisitePermissions)) {
      if (!prereqs.some(p => effective.has(p))) {
        missing.add(perm);
      }
    }
    return missing;
  }, [selectedPermissions, forcedPermissions, prerequisitePermissions]);

  // Permissions effectives = sélectionnées + forcées
  const effectivePermissions = useMemo(() => {
    const effective = new Set(selectedPermissions);
    forcedPermissions.forEach(p => effective.add(p));
    return [...effective];
  }, [selectedPermissions, forcedPermissions]);

  /** Ajoute les permissions impliquées et retire celles dont les prérequis ne sont plus remplis */
  const applyRules = (permissions: string[]): string[] => {
    // 1. Appliquer les implications (transitif)
    const result = new Set(permissions);
    let changed = true;
    while (changed) {
      changed = false;
      for (const [source, implied] of Object.entries(impliedPermissions)) {
        if (result.has(source)) {
          for (const p of implied) {
            if (!result.has(p)) {
              result.add(p);
              changed = true;
            }
          }
        }
      }
    }

    // 2. Retirer les permissions dont les prérequis ne sont plus remplis
    for (const [perm, prereqs] of Object.entries(prerequisitePermissions)) {
      if (result.has(perm) && !prereqs.some(p => result.has(p))) {
        result.delete(perm);
      }
    }

    return [...result];
  };

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const togglePermission = (code: string) => {
    // Ne pas permettre de décocher une permission forcée
    if (forcedPermissions.has(code)) return;
    // Ne pas permettre de cocher une permission sans prérequis
    if (missingPrerequisites.has(code)) return;

    const newPermissions = selectedPermissions.includes(code)
      ? selectedPermissions.filter(p => p !== code)
      : [...selectedPermissions, code];

    onChange(applyRules(newPermissions));
  };

  const toggleAllInCategory = (category: string) => {
    const categoryPerms = groupedPermissions[category].map(p => p.code);
    // Ne pas retirer les permissions forcées
    const toggleablePerms = categoryPerms.filter(p => !forcedPermissions.has(p));
    const allToggleableSelected = toggleablePerms.every(code => selectedPermissions.includes(code));

    let newPermissions: string[];
    if (allToggleableSelected) {
      // Décocher uniquement les permissions non-forcées de la catégorie
      newPermissions = selectedPermissions.filter(p => !toggleablePerms.includes(p));
    } else {
      newPermissions = [...new Set([...selectedPermissions, ...categoryPerms])];
    }

    onChange(applyRules(newPermissions));
  };

  const categoryLabels: Record<string, string> = {
    dashboard: 'Tableau de bord',
    admin_dashboard: 'Tableau de bord admin',
    society: 'Sociétés',
    repairer: 'Réparateurs',
    internal_user: 'Utilisateurs internes',
    external_user: 'Utilisateurs externes',
    warranty: wording.plural,
    contract: 'Contrats',
    delivery_proof: 'Attestations',
    sav: 'SAV',
    sav_admin: 'Administration SAV',
    invoice: 'Factures',
    catalog_pricing: 'Tarifications',
    invoice_fees: 'Frais de service',
    plv: 'PLV',
    webhooks: 'Webhooks',
    monthly_payment: 'Paiements',
    signature: 'Signatures',
    stripe: 'Stripe',
    upsell: 'UpSell',
    api_key: 'Clés API',
    news: 'Nouveautés',
    contract_payment: 'Mensualités des contrats',
    declaration_banner: 'Bandeau déclaration',
    integrity_check: "Checks d'intégrité",
  };

  return (
    <div className="tw-space-y-2 tw-max-h-96 tw-overflow-y-auto">
      {Object.entries(groupedPermissions).map(([category, permissions]) => {
        const isExpanded = expandedCategories.has(category);
        const allSelected = permissions.every(p => effectivePermissions.includes(p.code));
        const someSelected = permissions.some(p => effectivePermissions.includes(p.code));

        return (
          <div key={category} className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-gray-50">
              <button
                type="button"
                onClick={() => toggleCategory(category)}
                className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-gray-700 hover:tw-text-gray-900"
              >
                <span className="tw-text-xs">{isExpanded ? '\u25bc' : '\u25b6'}</span>
                {categoryLabels[category] || category}
              </button>
              <div className="tw-flex tw-items-center tw-gap-2">
                <span className="tw-text-xs tw-text-gray-500">
                  {permissions.filter(p => effectivePermissions.includes(p.code)).length}/{permissions.length}
                </span>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={() => toggleAllInCategory(category)}
                  className="tw-rounded tw-accent-affinicar-primary"
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                />
              </div>
            </div>

            {isExpanded && (
              <div className="tw-p-3 tw-space-y-2 tw-bg-white">
                {permissions.map(perm => {
                  const isForced = forcedPermissions.has(perm.code);
                  const isDisabledByPrereq = missingPrerequisites.has(perm.code);
                  const isDisabled = isForced || isDisabledByPrereq;
                  const isChecked = effectivePermissions.includes(perm.code);

                  return (
                    <label
                      key={perm.code}
                      className={`tw-flex tw-items-start tw-gap-3 tw-p-2 tw-rounded tw-cursor-pointer ${
                        isDisabled ? 'tw-bg-gray-50 tw-opacity-75' : 'hover:tw-bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => togglePermission(perm.code)}
                        disabled={isDisabled}
                        className="tw-mt-1 tw-rounded tw-accent-affinicar-primary disabled:tw-opacity-50"
                      />
                      <div className="tw-flex-1">
                        <div className="tw-flex tw-items-center tw-gap-1.5">
                          <span className="tw-text-sm tw-font-medium tw-text-gray-900">{perm.label}</span>
                          {isForced && <Lock size={12} className="tw-text-gray-400" />}
                          {isDisabledByPrereq && <Ban size={12} className="tw-text-gray-400" />}
                        </div>
                        {perm.description && (
                          <div className="tw-text-xs tw-text-gray-500 tw-mt-0.5">{perm.description}</div>
                        )}
                        {isForced && (
                          <div className="tw-text-xs tw-text-amber-600 tw-mt-0.5">Activé automatiquement</div>
                        )}
                        {isDisabledByPrereq && (
                          <div className="tw-text-xs tw-text-amber-600 tw-mt-0.5">Nécessite au moins une autre permission de cette catégorie</div>
                        )}
                        {showPermissionCode && <div className="tw-text-xs tw-text-gray-400 tw-mt-0.5 tw-font-mono">{perm.code}</div>}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
